
const CACHE_PREFIX = 'villmark_cache_';
const DEFAULT_TTL = 1000 * 60 * 30; // 30 minutter

export const CACHE_TTL = {
  SHORT: 1000 * 60 * 15,          // 15 min
  DEFAULT: DEFAULT_TTL,            // 30 min
  LONG: 1000 * 60 * 60 * 4,       // 4 timer
  OFFLINE: 1000 * 60 * 60 * 24 * 7 // 7 dager
};

// Flag to stop spamming console with quota errors once one has been logged
let hasLoggedQuotaError = false;

/**
 * Trygg stringify som håndterer sirkulære referanser og DOM-noder uten å krasje.
 */
const safeStringify = (obj: any): string => {
  const seen = new WeakSet();
  return JSON.stringify(obj, (key, value) => {
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) return "[Circular]";
      
      // Filtrer ut DOM noder og Leaflet objekter som ofte skaper trøbbel
      if (typeof Node !== 'undefined' && value instanceof Node) return "[DOM Node]";
      if (value.nodeType) return "[DOM Node]";
      if (value._leaflet_id !== undefined || value._map) return "[Leaflet Object]";
      
      seen.add(value);
    }
    return value;
  });
};

/**
 * Defensiv saniteringsfunksjon som kun tillater enkle data-typer (POJOs).
 */
const sanitizeForCache = (val: any): any => {
  const seen = new WeakSet();
  
  const clean = (item: any): any => {
    if (item === null || typeof item !== 'object') {
      return item;
    }
    
    if (seen.has(item)) return undefined;
    
    const constructorName = item.constructor?.name;
    const isLeaflet = item._leaflet_id !== undefined || item._map || (constructorName && constructorName.length <= 3);
    const isDOM = item.nodeType || item instanceof HTMLElement || (typeof window !== 'undefined' && item === window);
    
    if (isLeaflet || isDOM) return undefined;

    seen.add(item);
    
    if (Array.isArray(item)) {
      return item.map(clean).filter(i => i !== undefined);
    }
    
    if (Object.prototype.toString.call(item) !== '[object Object]') {
      return undefined;
    }

    const result: any = {};
    for (const key in item) {
      if (Object.prototype.hasOwnProperty.call(item, key)) {
        if (typeof item[key] === 'function' || key.startsWith('_')) continue;
        const cleaned = clean(item[key]);
        if (cleaned !== undefined) {
          result[key] = cleaned;
        }
      }
    }
    return result;
  };

  return clean(val);
};

export const clearExpiredCache = () => {
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith(CACHE_PREFIX)) {
      try {
        const itemStr = localStorage.getItem(key);
        if (!itemStr) {
           localStorage.removeItem(key);
           return;
        }
        const item = JSON.parse(itemStr);
        if (item.expiry && Date.now() > item.expiry) {
          localStorage.removeItem(key);
        }
      } catch (e) {
        localStorage.removeItem(key);
      }
    }
  });
};

const nuclearClearCache = () => {
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith(CACHE_PREFIX)) {
      localStorage.removeItem(key);
    }
  });
};

export const setCache = (key: string, data: any, ttl = DEFAULT_TTL) => {
  try {
    const sanitizedData = sanitizeForCache(data);
    const item = {
      data: sanitizedData,
      expiry: Date.now() + ttl,
    };
    
    const serialized = safeStringify(item);
    
    try {
      localStorage.setItem(CACHE_PREFIX + key, serialized);
    } catch (e: any) {
      if (e.name === 'QuotaExceededError' || e.code === 22 || e.number === -2147024882) {
        // Prøv å rydde og lagre igjen
        clearExpiredCache();
        try {
          localStorage.setItem(CACHE_PREFIX + key, serialized);
        } catch (retryError) {
           // Hvis det fortsatt feiler, tøm alt
           nuclearClearCache();
           try { 
             localStorage.setItem(CACHE_PREFIX + key, serialized); 
           } catch(finalErr) {
             // Hvis det fortsatt feiler, gi opp stille for å unngå spam
             if (!hasLoggedQuotaError) {
                console.warn("Storage full. Caching disabled temporarily.");
                hasLoggedQuotaError = true;
             }
           }
        }
      }
    }
  } catch (e) {
    // Generell feil under serialisering eller lagring
    // Ignorer stille
  }
};

export const getCache = (key: string) => {
  const itemStr = localStorage.getItem(CACHE_PREFIX + key);
  if (!itemStr) return null;

  try {
    const item = JSON.parse(itemStr);
    if (Date.now() > item.expiry) {
      // Ikke slett — data kan være nyttig offline. Returner null for å trigge ny henting.
      return null;
    }
    return item.data;
  } catch (e) {
    localStorage.removeItem(CACHE_PREFIX + key);
    return null;
  }
};

/**
 * Returnerer cachet data selv om TTL er utløpt (stale).
 * Nyttig for stale-while-revalidate og offline-scenarioer.
 */
export const getCacheStale = (key: string): { data: any; isStale: boolean } => {
  const itemStr = localStorage.getItem(CACHE_PREFIX + key);
  if (!itemStr) return { data: null, isStale: false };

  try {
    const item = JSON.parse(itemStr);
    const isStale = Date.now() > item.expiry;
    return { data: item.data, isStale };
  } catch (e) {
    localStorage.removeItem(CACHE_PREFIX + key);
    return { data: null, isStale: false };
  }
};